import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DndContext, DragOverlay, pointerWithin } from "@dnd-kit/core";
import {
	SortableContext,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
	ROOT_PARENT,
	ROOT_DROP_ZONE_ID,
	ROOT_START_DROP_ZONE_ID,
	ROOT_END_DROP_ZONE_ID,
	findContainer,
} from "../utilities/dragUtils";
import { SortablePageRow } from "./SortablePageRow";
import { DroppableFolder } from "./DroppableFolder";
import { NodeDragOverlay } from "./NodeDragOverlay";
import { RootDropZone } from "./RootDropZone";
import { RootEndDropZone, RootStartDropZone } from "./RootEdgeDropZones";

/** @typedef {import("../types").InfoNode} InfoNode */
/** @typedef {import("../types").InfoNodeItemsMap} InfoNodeItemsMap */
/** @typedef {import("../types").InfoNodeMap} InfoNodeMap */

/**
 * Renders the draggable list of info pages/folders along with drag hint text.
 * @param {{
 *   items: InfoNodeItemsMap,
 *   nodesById: InfoNodeMap,
 *   canAdmin: boolean,
 *   activeId: string | null,
 *   overId: string | null,
 *   activeNode: InfoNode | undefined,
 *   onDeleteNode: (id: string) => void,
 *   onEditFolder: (node: InfoNode) => void,
 *   onDragStart: (event: any) => void,
 *   onDragMove: (event: any) => void,
 *   onDragEnd: (event: any) => void,
 *   onDragCancel: () => void,
 *   onCreatePage?: (parentId: string) => void,
 *   onEditNode: (nodeId: string) => void,
 * }} props
 */
export function Rows({
	items,
	nodesById,
	canAdmin,
	activeId,
	overId,
	activeNode,
	onDeleteNode,
	onEditFolder,
	onDragStart,
	onDragMove,
	onDragEnd,
	onDragCancel,
	onCreatePage,
	onEditNode,
}) {
	const rootItemIds = items[ROOT_PARENT] || [];
	const [collapsedFolders, setCollapsedFolders] = useState(() => new Set());

	useEffect(() => {
		setCollapsedFolders((prev) => {
			const next = new Set(prev);
			let changed = false;
			for (const folderId of prev) {
				if (!items[folderId]) {
					next.delete(folderId);
					changed = true;
				}
			}
			return changed ? next : prev;
		});
	}, [items]);

	const overContainerId = useMemo(() => {
		if (
			overId === ROOT_DROP_ZONE_ID ||
			overId === ROOT_START_DROP_ZONE_ID ||
			overId === ROOT_END_DROP_ZONE_ID
		) {
			return ROOT_PARENT;
		}

		return findContainer(items, overId);
	}, [items, overId]);

	const activeChildCount =
		activeNode && activeNode.type === "folder"
			? (items[activeNode.RowId] || []).length
			: 0;
	const projectedDepth =
		activeNode && activeNode.parentId !== ROOT_PARENT ? 1 : 0;

	const isDraggingNestedPage = Boolean(
		activeNode &&
			activeNode.type === "page" &&
			activeNode.parentId &&
			activeNode.parentId !== ROOT_PARENT,
	);
	const activePageTitle =
		activeNode && activeNode.type === "page" ? activeNode.title : null;
	const activeParentNode =
		activeNode && activeNode.parentId && activeNode.parentId !== ROOT_PARENT
			? nodesById.get(activeNode.parentId)
			: null;
	const showRootDropZone = canAdmin && isDraggingNestedPage;
	const handleToggleFolder = useCallback((folderId) => {
		setCollapsedFolders((prev) => {
			const next = new Set(prev);
			if (next.has(folderId)) {
				next.delete(folderId);
			} else {
				next.add(folderId);
			}
			return next;
		});
	}, []);

	return (
		<div className="infopage__rows">
			<DndContext
				collisionDetection={pointerWithin}
				onDragStart={onDragStart}
				onDragMove={onDragMove}
				onDragEnd={onDragEnd}
				onDragCancel={onDragCancel}
			>
				<SortableContext
					items={rootItemIds}
					strategy={verticalListSortingStrategy}
				>
					<div className="root-dropzone-stack">
						<RootStartDropZone />
						<RootDropZone
							isVisible={showRootDropZone}
							isDisabled={!showRootDropZone}
							pageTitle={activePageTitle}
							folderTitle={activeParentNode ? activeParentNode.title : null}
						/>
					</div>
					{rootItemIds.map((nodeId) => {
						const node = nodesById.get(nodeId);
						if (!node) return null;

						if (node.type === "folder") {
							const childIds = items[node.RowId] || [];
							const shouldHighlightNest = Boolean(
								activeNode &&
									activeNode.type === "page" &&
									overContainerId === node.RowId,
							);
							return (
								<DroppableFolder
									key={node.RowId}
									node={node}
									childIds={childIds}
									canAdmin={canAdmin}
									onDeleteNode={() => onDeleteNode(node.RowId)}
									onEditFolder={() => onEditFolder(node)}
									isCollapsed={collapsedFolders.has(node.RowId)}
									onToggleCollapse={() => handleToggleFolder(node.RowId)}
									isNestTarget={shouldHighlightNest}
									onCreateChildPage={onCreatePage}
								>
									{childIds.map((childId) => {
										const childNode = nodesById.get(childId);
										if (!childNode) return null;
										return (
											<SortablePageRow
												key={childNode.RowId}
												node={childNode}
												canAdmin={canAdmin}
												onDeleteNode={() => onDeleteNode(childNode.RowId)}
												onEditNode={onEditNode}
											/>
										);
									})}
								</DroppableFolder>
							);
						}

						return (
							<SortablePageRow
								key={node.RowId}
								node={node}
								canAdmin={canAdmin}
								onDeleteNode={() => onDeleteNode(node.RowId)}
								onEditNode={onEditNode}
							/>
						);
					})}
				</SortableContext>
				<RootEndDropZone />
				<DragOverlay dropAnimation={null}>
					{activeNode ? (
						<NodeDragOverlay
							node={activeNode}
							projectedDepth={projectedDepth}
							childCount={activeChildCount}
						/>
					) : null}
				</DragOverlay>
			</DndContext>
		</div>
	);
}
